import { Button } from '@/components/ui/button'
import { useAskQuestion, useQAHistory } from '@/hooks/use-learning'
import type { ContextPosition, LearningQA } from '@/types'
import { ChevronDown, ChevronUp, Loader2, MessageCircle, Send, X } from 'lucide-react'
import { type FormEvent, useCallback, useRef, useState } from 'react'
import Markdown from 'react-markdown'

interface QAPanelProps {
  moduleId: number
  moduleTitle: string
  getScrollPercent: () => number
  isOpen: boolean
  onToggle: () => void
}

function QAItem({ qa, isNew }: { qa: LearningQA; isNew?: boolean }) {
  const [expanded, setExpanded] = useState(isNew)

  return (
    <div className={`border rounded-lg p-3 ${isNew ? 'border-brand bg-brand/5' : ''}`}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left flex items-start justify-between gap-2"
      >
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm line-clamp-2">{qa.question}</p>
          {qa.context_position?.scroll_percent != null && (
            <p className="text-xs text-muted-foreground mt-1">
              Asked at {qa.context_position.scroll_percent}% of content
            </p>
          )}
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        )}
      </button>
      {expanded && (
        <div className="mt-3 pt-3 border-t">
          <div className="prose prose-sm max-w-none text-sm">
            <Markdown>{qa.answer}</Markdown>
          </div>
        </div>
      )}
    </div>
  )
}

export function QAPanel({
  moduleId,
  moduleTitle,
  getScrollPercent,
  isOpen,
  onToggle,
}: QAPanelProps) {
  const [question, setQuestion] = useState('')
  const [newQAId, setNewQAId] = useState<number | null>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const { data: qaHistory, isLoading: historyLoading } = useQAHistory(moduleId)
  const askQuestion = useAskQuestion(moduleId)

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      if (!question.trim() || askQuestion.isPending) return

      const scrollPercent = getScrollPercent()
      const contextPosition: ContextPosition = {
        type: 'text',
        scroll_percent: scrollPercent,
      }

      askQuestion.mutate(
        { question: question.trim(), contextPosition },
        {
          onSuccess: (qa) => {
            setQuestion('')
            setNewQAId(qa.id)
            // Clear "new" highlight after 5 seconds
            setTimeout(() => setNewQAId(null), 5000)
          },
        },
      )
    },
    [question, askQuestion, getScrollPercent],
  )

  // Focus input when panel opens
  const handleOpen = useCallback(() => {
    onToggle()
    if (!isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen, onToggle])

  if (!isOpen) {
    const qaCount = qaHistory?.total ?? 0
    return (
      <button
        type="button"
        onClick={handleOpen}
        className="fixed bottom-6 right-4 z-20 flex items-center gap-2 rounded-full bg-brand p-4 text-white shadow-lg transition-all hover:bg-brand/90 md:right-[calc(0.5rem+var(--coach-panel-width,420px)+0.5rem+0.375rem)]"
        title="Ask a question about this lesson"
      >
        <MessageCircle className="h-5 w-5" />
        <span className="text-sm font-medium">Ask</span>
        {qaCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center rounded-full bg-brand-dark text-white text-[10px] font-bold px-1">
            {qaCount}
          </span>
        )}
      </button>
    )
  }

  return (
    <div className="fixed bottom-0 right-0 z-30 flex max-h-[70vh] w-full flex-col rounded-tl-xl border-l border-t bg-background shadow-xl sm:w-96 md:right-[calc(0.5rem+var(--coach-panel-width,420px))]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Ask about this lesson
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{moduleTitle}</p>
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="p-1 hover:bg-muted rounded-md transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Question Input */}
      <form onSubmit={handleSubmit} className="p-4 border-b">
        <div className="relative">
          <textarea
            ref={inputRef}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What would you like to know about this lesson?"
            className="w-full min-h-[80px] p-3 pr-12 text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand/50"
            disabled={askQuestion.isPending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit(e)
              }
            }}
          />
          <Button
            type="submit"
            size="sm"
            disabled={!question.trim() || askQuestion.isPending}
            className="absolute bottom-3 right-3"
          >
            {askQuestion.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        {askQuestion.isError && (
          <p className="text-xs text-destructive mt-2">Failed to get answer. Please try again.</p>
        )}
      </form>

      {/* Q&A History */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {historyLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : qaHistory?.items?.length ? (
          <>
            <p className="text-xs text-muted-foreground">
              {qaHistory.total} question{qaHistory.total !== 1 ? 's' : ''} asked
            </p>
            {qaHistory.items.map((qa) => (
              <QAItem key={qa.id} qa={qa} isNew={qa.id === newQAId} />
            ))}
          </>
        ) : (
          <div className="text-center py-8">
            <MessageCircle className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              No questions yet. Ask anything about this lesson!
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// Floating Ask Button (for when panel is closed)
export function AskButton({ onClick, count }: { onClick: () => void; count?: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="fixed bottom-6 right-4 z-20 flex items-center gap-2 rounded-full bg-brand p-4 text-white shadow-lg transition-all hover:bg-brand/90 md:right-[calc(0.5rem+var(--coach-panel-width,420px)+0.5rem+0.375rem)]"
      title="Ask a question about this lesson"
    >
      <MessageCircle className="h-5 w-5" />
      <span className="text-sm font-medium">Ask</span>
      {count != null && count > 0 && (
        <span className="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center rounded-full bg-brand-dark text-white text-[10px] font-bold px-1">
          {count}
        </span>
      )}
    </button>
  )
}
